/**
 * JODestockageEnhancer — Upgrades the default cockpit shop -X% badge
 * into a more visible "DÉSTOCKAGE" red pill, and overlays the admin
 * custom badge text (e.g. "Dernières pièces") on matching product cards.
 *
 * Strategy : on shop page, fetch the destockage list (slug → badge_text)
 * then walk product cards via their `a[href="/shop/<slug>"]` anchor and
 * inject overlays. Idempotent + MutationObserver to catch filter refresh.
 *
 * Renderless.
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MARKER = "data-jdo-destockage-enhanced";

export default function JODestockageEnhancer() {
  const location = useLocation();
  const overridesRef = useRef({});       // slug → badge_text

  // Fetch overrides once (and again when shop page navigated to)
  useEffect(() => {
    if (!location.pathname.startsWith("/shop")) return;
    axios.get(`${API}/jardins-ole/shop/destockage`)
      .then((r) => {
        const map = {};
        for (const it of (r.data?.items || [])) {
          map[it.slug] = {
            badge_text: it.badge_text || "",
            lowStock: it.stock_tracking && it.stock_qty <= 5,
            stock_qty: it.stock_qty,
            views_today: it.views_today || 0,
          };
        }
        overridesRef.current = map;
        enhance();
      })
      .catch(() => {});
  }, [location.pathname]);

  // Enhance function — find product cards & inject DÉSTOCKAGE + custom text
  const enhance = () => {
    const cards = document.querySelectorAll('a[href^="/shop/"]');
    cards.forEach((a) => {
      if (a.getAttribute(MARKER) === "yes") return;
      const slug = (a.getAttribute("href") || "").replace(/^\/shop\//, "").split("?")[0].split("#")[0];
      if (!slug || slug.includes("/")) return;
      const meta = overridesRef.current[slug];
      // Only enhance if this product is in destockage
      if (!meta) return;
      // Find the existing -X% badge inside this card and upgrade it
      const oldBadge = a.querySelector(".absolute.top-2.left-1\\/2") ||
                       Array.from(a.querySelectorAll("span")).find(
                         (s) => /^[\u2212-]\d+%$/.test((s.textContent || "").trim())
                       );
      if (oldBadge) {
        const pctText = (oldBadge.textContent || "").trim();
        oldBadge.classList.remove("bg-emerald-500");
        oldBadge.classList.add("jdo-destockage-badge");
        oldBadge.innerHTML = `<span class="jdo-destockage-badge-label">DÉSTOCKAGE</span><span class="jdo-destockage-badge-pct">${pctText}</span>`;
      }
      // Inject custom badge text (e.g. "Dernières pièces") if any
      if (meta.badge_text) {
        const existing = a.querySelector(".jdo-destockage-custom-pill");
        if (!existing) {
          // Find the image container (first child div with class containing relative/aspect)
          const imgContainer = a.querySelector("[class*='aspect-square'], [class*='aspect-']") || a.firstElementChild;
          if (imgContainer) {
            const pill = document.createElement("span");
            pill.className = "jdo-destockage-custom-pill";
            pill.textContent = meta.badge_text;
            imgContainer.appendChild(pill);
          }
        }
      }
      // Low-stock pill
      if (meta.lowStock) {
        const existing = a.querySelector(".jdo-destockage-lowstock-pill");
        if (!existing) {
          const imgContainer = a.querySelector("[class*='aspect-square'], [class*='aspect-']") || a.firstElementChild;
          if (imgContainer) {
            const pill = document.createElement("span");
            pill.className = "jdo-destockage-lowstock-pill";
            pill.innerHTML = `<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg> Plus que ${meta.stock_qty}`;
            imgContainer.appendChild(pill);
          }
        }
      }
      // Views-today pill (urgency social proof)
      if (meta.views_today >= 3) {
        const existing = a.querySelector(".jdo-destockage-views-pill");
        if (!existing) {
          const imgContainer = a.querySelector("[class*='aspect-square'], [class*='aspect-']") || a.firstElementChild;
          if (imgContainer) {
            const pill = document.createElement("span");
            pill.className = "jdo-destockage-views-pill";
            pill.textContent = `👁 ${meta.views_today} vue${meta.views_today > 1 ? "s" : ""} aujourd'hui`;
            imgContainer.appendChild(pill);
          }
        }
      }
      a.setAttribute(MARKER, "yes");
    });
  };

  useEffect(() => {
    if (!location.pathname.startsWith("/shop")) return;
    enhance();
    const observer = new MutationObserver(() => enhance());
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [location.pathname]);

  return null;
}
